import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ComposerStateService,
  ModelConfigService,
  ModelElement,
} from '@app/model-composer';
import { ModelElementType } from '@app/model-composer';
import { ElementCardComponent } from '@app/model-composer/components/element-card.component';
import { ConnectionSide } from '@app/model-composer/enums/connection-side.enum';
import {TranslatePipe, TranslateService} from "@ngx-translate/core";

type AccordionSection = 'left' | 'central' | 'right';

@Component({
  selector: 'app-element-selector',
  standalone: true,
  imports: [CommonModule, ElementCardComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="ipe:flex ipe:h-full ipe:min-h-0 ipe:flex-col ipe:bg-white">
      <div class="ipe:border-b ipe:border-[#ece7e2] ipe:px-6 ipe:py-5">
        <div class="ipe:flex ipe:flex-col ipe:gap-4">
          <div class="ipe:flex ipe:items-center ipe:justify-between ipe:gap-4">
            <div class="ipe:min-w-0">
              <p class="ipe:mb-1 ipe:text-[11px] ipe:uppercase ipe:tracking-[0.18em] ipe:text-[#9a8c82] font-walter">
                {{ 'composition.composition' | translate }}
              </p>
              <div class="ipe:flex ipe:flex-wrap ipe:items-center ipe:gap-x-3 ipe:gap-y-1">
                <span class="ipe:text-sm ipe:text-[#3f312f]">
                  {{ compositionCount() }} / {{ maxElements }} {{ 'composition.added_elements' | translate | lowercase }}
                </span>

                @if (isAtLimit()) {
                  <span
                    class="
                      ipe:inline-flex ipe:items-center ipe:border
                      ipe:border-[#e7d8d3] ipe:bg-[#fcf4f2]
                      ipe:px-2 ipe:py-1
                      ipe:text-xs ipe:font-medium ipe:text-[#8d5a56]
                    "
                  >
                    {{ 'composition.limit_reached' | translate }}
                  </span>
                }
              </div>
            </div>

            <div class="ipe:relative ipe:w-full ipe:max-w-[28rem]">
              <input
                type="text"
                [value]="query()"
                (input)="setQuery($event)"
                placeholder="{{ 'composition.search' | translate }}"
                class="
                  ipe:h-11 ipe:w-full
                  ipe:border ipe:border-[#e7e1db]
                  ipe:bg-[#fcfbfa]
                  ipe:px-4 ipe:pr-10
                  ipe:text-sm ipe:text-[#3f312f]
                  ipe:placeholder:text-[#a1938a]
                  ipe:outline-none ipe:transition
                  focus:ipe:border-black
                  focus:ipe:bg-white
                "
              />

              @if (query()) {
                <button
                  type="button"
                  (click)="clearQuery()"
                  class="
                    ipe:absolute ipe:right-2 ipe:top-1/2 ipe:-translate-y-1/2
                    ipe:flex ipe:h-7 ipe:w-7 ipe:items-center ipe:justify-center
                    ipe:border ipe:border-transparent
                    ipe:text-[#8f8178] ipe:transition
                    hover:ipe:border-[#d8d1ca]
                    hover:ipe:bg-white
                    hover:ipe:text-black
                    ipe:cursor-pointer
                  "
                  aria-label="Pulisci ricerca"
                  title="Pulisci"
                >
                  ✕
                </button>
              }
            </div>
          </div>

          @if (globalHint(); as hint) {
            <div
              class="
                ipe:flex ipe:items-start ipe:gap-2
                ipe:border ipe:border-[#e7d8d3]
                ipe:bg-[#fcf4f2]
                ipe:px-3 ipe:py-2
                ipe:text-sm ipe:text-[#8d5a56]
              "
            >
              <span class="ipe:mt-[1px]">ⓘ</span>
              <span>{{ hint }}</span>
            </div>
          }
        </div>
      </div>

      <div class="ipe:flex-1 ipe:min-h-0 ipe:overflow-y-auto ipe:px-6 ipe:py-5">
        @if (hasAny()) {
          <div class="ipe:flex ipe:flex-col ipe:gap-4">
            @if (left().length) {
              <section class="ipe:border ipe:border-[#ece7e2] ipe:bg-white">
                <button
                  type="button"
                  (click)="toggleSection('left')"
                  class="
                    ipe:cursor-pointer
                    ipe:flex ipe:w-full ipe:items-center ipe:justify-between ipe:gap-4
                    ipe:border-[#ece7e2]
                    ipe:bg-[#fcfbfa] ipe:px-4 ipe:py-3
                    ipe:text-left ipe:transition
                    hover:ipe:bg-white
                  "
                  [class.ipe:border-b]="isOpen('left')"
                >
                  <div class="ipe:flex ipe:min-w-0 ipe:flex-col ipe:gap-1">
                    <span class="ipe:text-[11px] ipe:uppercase ipe:tracking-[0.18em] ipe:text-[#9a8c82] font-walter">
                      {{ 'composition.group' | translate }}
                    </span>
                    <span class="ipe:text-sm ipe:font-semibold ipe:text-[#3f312f]">
                      {{ 'composition.left_elements' | translate }}
                    </span>
                  </div>

                  <div class="ipe:flex ipe:items-center ipe:gap-3">
                    <span
                      class="
                        ipe:rounded-sm
                        ipe:inline-flex ipe:min-w-8 ipe:items-center ipe:justify-center
                        ipe:border ipe:border-[#e6dfd9]
                        ipe:bg-white ipe:px-2 ipe:py-1
                        ipe:text-xs ipe:font-medium ipe:text-[#7f726a]
                      "
                    >
                      {{ left().length }}
                    </span>

                    <span
                      class="ipe:text-[#8d5a56] ipe:transition-transform"
                      [class.ipe:rotate-180]="isOpen('left')"
                    >
                      ▾
                    </span>
                  </div>
                </button>

                @if (isOpen('left')) {
                  <div class="ipe:p-4 ipe:pt-3">
                    @if (sectionHint('left'); as hint) {
                      <div
                        class="
                          ipe:mb-4 ipe:flex ipe:items-start ipe:gap-2
                          ipe:border ipe:border-[#ece7e2]
                          ipe:bg-[#fcfbfa]
                          ipe:px-3 ipe:py-2
                          ipe:text-sm ipe:text-[#6f6259]
                        "
                      >
                        <span class="ipe:mt-[1px]">ⓘ</span>
                        <span>{{ hint }}</span>
                      </div>
                    }

                    <div class="ipe:grid ipe:grid-cols-1 ipe:gap-5 ipe:md:grid-cols-2 ipe:xl:grid-cols-3">
                      @for (el of left(); track el.id) {
                        <app-element-card
                          class="ipe:block ipe:min-w-0"
                          [element]="el"
                          [disabled]="isDisabled(el)"
                          (select)="onSelect($event)"
                        />
                      }
                    </div>
                  </div>
                }
              </section>
            }

            @if (central().length) {
              <section class="ipe:border ipe:border-[#ece7e2] ipe:bg-white">
                <button
                  type="button"
                  (click)="toggleSection('central')"
                  class="
                    ipe:cursor-pointer
                    ipe:flex ipe:w-full ipe:items-center ipe:justify-between ipe:gap-4
                    ipe:border-[#ece7e2]
                    ipe:bg-[#fcfbfa] ipe:px-4 ipe:py-3
                    ipe:text-left ipe:transition
                    hover:ipe:bg-white
                  "
                  [class.ipe:border-b]="isOpen('central')"
                >
                  <div class="ipe:flex ipe:min-w-0 ipe:flex-col ipe:gap-1">
                    <span class="ipe:text-[11px] ipe:uppercase ipe:tracking-[0.18em] ipe:text-[#9a8c82] font-walter">
                      {{ 'composition.group' | translate }}
                    </span>
                    <span class="ipe:text-sm ipe:font-semibold ipe:text-[#3f312f]">
                      {{ 'composition.central_elements' | translate }}
                    </span>
                  </div>

                  <div class="ipe:flex ipe:items-center ipe:gap-3">
                    <span
                      class="
                        ipe:rounded-sm
                        ipe:inline-flex ipe:min-w-8 ipe:items-center ipe:justify-center
                        ipe:border ipe:border-[#e6dfd9]
                        ipe:bg-white ipe:px-2 ipe:py-1
                        ipe:text-xs ipe:font-medium ipe:text-[#7f726a]
                      "
                    >
                      {{ central().length }}
                    </span>

                    <span
                      class="ipe:text-[#8d5a56] ipe:transition-transform"
                      [class.ipe:rotate-180]="isOpen('central')"
                    >
                      ▾
                    </span>
                  </div>
                </button>

                @if (isOpen('central')) {
                  <div class="ipe:p-4 ipe:pt-3">
                    @if (sectionHint('central'); as hint) {
                      <div
                        class="
                          ipe:mb-4 ipe:flex ipe:items-start ipe:gap-2
                          ipe:border ipe:border-[#ece7e2]
                          ipe:bg-[#fcfbfa]
                          ipe:px-3 ipe:py-2
                          ipe:text-sm ipe:text-[#6f6259]
                        "
                      >
                        <span class="ipe:mt-[1px]">ⓘ</span>
                        <span>{{ hint }}</span>
                      </div>
                    }

                    <div class="ipe:grid ipe:grid-cols-1 ipe:gap-5 ipe:md:grid-cols-2 ipe:xl:grid-cols-3">
                      @for (el of central(); track el.id) {
                        <app-element-card
                          class="ipe:block ipe:min-w-0"
                          [element]="el"
                          [disabled]="isDisabled(el)"
                          (select)="onSelect($event)"
                        />
                      }
                    </div>
                  </div>
                }
              </section>
            }

            @if (right().length) {
              <section class="ipe:border ipe:border-[#ece7e2] ipe:bg-white">
                <button
                  type="button"
                  (click)="toggleSection('right')"
                  class="
                    ipe:cursor-pointer
                    ipe:flex ipe:w-full ipe:items-center ipe:justify-between ipe:gap-4
                    ipe:border-[#ece7e2]
                    ipe:bg-[#fcfbfa] ipe:px-4 ipe:py-3
                    ipe:text-left ipe:transition
                    hover:ipe:bg-white
                  "
                  [class.ipe:border-b]="isOpen('right')"
                >
                  <div class="ipe:flex ipe:min-w-0 ipe:flex-col ipe:gap-1">
                    <span class="ipe:text-[11px] ipe:uppercase ipe:tracking-[0.18em] ipe:text-[#9a8c82] font-walter">
                      {{ 'composition.group' | translate }}
                    </span>
                    <span class="ipe:text-sm ipe:font-semibold ipe:text-[#3f312f]">
                      {{ 'composition.right_elements' | translate }}
                    </span>
                  </div>

                  <div class="ipe:flex ipe:items-center ipe:gap-3">
                    <span
                      class="
                        ipe:rounded-sm
                        ipe:inline-flex ipe:min-w-8 ipe:items-center ipe:justify-center
                        ipe:border ipe:border-[#e6dfd9]
                        ipe:bg-white ipe:px-2 ipe:py-1
                        ipe:text-xs ipe:font-medium ipe:text-[#7f726a]
                      "
                    >
                      {{ right().length }}
                    </span>

                    <span
                      class="ipe:text-[#8d5a56] ipe:transition-transform"
                      [class.ipe:rotate-180]="isOpen('right')"
                    >
                      ▾
                    </span>
                  </div>
                </button>

                @if (isOpen('right')) {
                  <div class="ipe:p-4 ipe:pt-3">
                    @if (sectionHint('right'); as hint) {
                      <div
                        class="
                          ipe:mb-4 ipe:flex ipe:items-start ipe:gap-2
                          ipe:border ipe:border-[#ece7e2]
                          ipe:bg-[#fcfbfa]
                          ipe:px-3 ipe:py-2
                          ipe:text-sm ipe:text-[#6f6259]
                        "
                      >
                        <span class="ipe:mt-[1px]">ⓘ</span>
                        <span>{{ hint }}</span>
                      </div>
                    }

                    <div class="ipe:grid ipe:grid-cols-1 ipe:gap-5 ipe:md:grid-cols-2 ipe:xl:grid-cols-3">
                      @for (el of right(); track el.id) {
                        <app-element-card
                          class="ipe:block ipe:min-w-0"
                          [element]="el"
                          [disabled]="isDisabled(el)"
                          (select)="onSelect($event)"
                        />
                      }
                    </div>
                  </div>
                }
              </section>
            }
          </div>
        } @else {
          <div
            class="
              ipe:flex ipe:h-full ipe:min-h-[16rem] ipe:items-center ipe:justify-center
              ipe:border ipe:border-dashed ipe:border-[#e5ddd6]
              ipe:bg-[#fcfbfa]
              ipe:text-sm ipe:text-[#8f8178]
            "
          >
            Nessun elemento trovato
          </div>
        }
      </div>
    </section>
  `,
})
export class ElementSelectorComponent {
  readonly state = inject(ComposerStateService);
  readonly config = inject(ModelConfigService);
  readonly _translate = inject(TranslateService);

  readonly maxElements = 8;

  private readonly _query = signal('');
  readonly query = this._query.asReadonly();

  private readonly _openSection = signal<AccordionSection | null>(null);
  readonly openSection = this._openSection.asReadonly();

  readonly compositionCount = computed(() => this.state.composition().length);
  readonly isAtLimit = computed(() => this.compositionCount() >= this.maxElements);

  readonly allElements = computed<ModelElement[]>(() => this.config.config()?.elements ?? []);

  readonly filteredElements = computed<ModelElement[]>(() => {
    const q = this.query().trim().toLowerCase();
    const list = this.allElements();

    if (!q) {
      return list;
    }

    return list.filter((el) => {
      const name = (el.name ?? '').toLowerCase();
      const dims = (el.dimensions ?? '').toLowerCase();
      const typeLabel = (el.typeLabel ?? '').toLowerCase();
      return name.includes(q) || dims.includes(q) || typeLabel.includes(q);
    });
  });

  private readonly grouped = computed(() => {
    const left: ModelElement[] = [];
    const central: ModelElement[] = [];
    const right: ModelElement[] = [];

    for (const el of this.filteredElements()) {
      switch (el.type as ModelElementType | string) {
        case ModelElementType.Left:
        case 'L':
          left.push(el);
          break;
        case ModelElementType.Central:
        case 'C':
          central.push(el);
          break;
        case ModelElementType.Right:
        case 'R':
          right.push(el);
          break;
        default:
          central.push(el);
          break;
      }
    }

    return {
      [ModelElementType.Left]: left,
      [ModelElementType.Central]: central,
      [ModelElementType.Right]: right,
    };
  });

  readonly left = computed(() => this.grouped()[ModelElementType.Left]);
  readonly central = computed(() => this.grouped()[ModelElementType.Central]);
  readonly right = computed(() => this.grouped()[ModelElementType.Right]);
  readonly hasAny = computed(() => this.filteredElements().length > 0);

  setQuery(ev: Event): void {
    this._query.set((ev.target as HTMLInputElement).value ?? '');
    this.ensureOpenSectionIsValid();
  }

  clearQuery(): void {
    this._query.set('');
    this.ensureOpenSectionIsValid();
  }

  isDisabled(el: ModelElement): boolean {
    return !this.state.activeSide() || !this.state.canSelect(el) || this.isAtLimit();
  }

  onSelect(el: ModelElement): void {
    this.state.add(el);
  }

  isOpen(section: AccordionSection): boolean {
    return this._openSection() === section;
  }

  toggleSection(section: AccordionSection): void {
    if (this._openSection() === section) {
      this._openSection.set(null);
      return;
    }

    this._openSection.set(section);
  }

  globalHint(): string | null {
    if (this.compositionCount() === 0) {
      return null;
    }

    if (this.isAtLimit()) {
      return `${this.getTranslation('composition.limit_reached_desc_1')} (${this.maxElements}).
              ${this.getTranslation('composition.limit_reached_desc_2')}`;
    }

    const activeSide = this.state.activeSide();

    if (activeSide === ConnectionSide.Left) {
      return this.getTranslation('composition.left_selected');
    }

    if (activeSide === ConnectionSide.Right) {
      return this.getTranslation('composition.right_selected');
    }

    return null;
  }

  sectionHint(section: AccordionSection): string | null {
    if (this.compositionCount() === 0) {
      return this.getTranslation('composition.pickup_and_start');
    }

    if (this.isAtLimit()) {
      return `Hai già inserito ${this.compositionCount()} elementi su ${this.maxElements} disponibili.`;
    }

    const activeSide = this.state.activeSide();

    if (section === 'left') {
      if (activeSide === ConnectionSide.Left && this.state.canAddLeft()) {
        return 'Puoi aggiungere un elemento collegandolo al lato sinistro selezionato.';
      }

      if (activeSide === ConnectionSide.Right) {
        return this.getTranslation('composition.left_side_not_available');
      }

      if (!this.state.canAddLeft()) {
        return 'Al momento non è possibile estendere la composizione verso sinistra con un elemento compatibile.';
      }

      return 'Seleziona un collegamento valido nella composizione per aggiungere un elemento laterale sinistro.';
    }

    if (section === 'right') {
      if (activeSide === ConnectionSide.Right && this.state.canAddRight()) {
        return 'Puoi aggiungere un elemento collegandolo al lato destro selezionato.';
      }

      if (activeSide === ConnectionSide.Left) {
        return this.getTranslation('composition.right_side_not_available');
      }

      if (!this.state.canAddRight()) {
        return 'Al momento non è possibile estendere la composizione verso destra con un elemento compatibile.';
      }

      return 'Seleziona un collegamento valido nella composizione per aggiungere un elemento laterale destro.';
    }

    if (activeSide === ConnectionSide.Left) {
      return 'Stai lavorando sul lato sinistro della composizione. Tutti gli elementi centrali restano comunque selezionabili se compatibili.';
    }

    if (activeSide === ConnectionSide.Right) {
      return 'Stai lavorando sul lato destro della composizione. Tutti gli elementi centrali restano comunque selezionabili se compatibili.';
    }

    return 'Scegli un elemento centrale compatibile con la composizione attuale.';
  }

  private ensureOpenSectionIsValid(): void {
    const current = this._openSection();

    if (current === null) {
      return;
    }

    if (current === 'left' && this.left().length) {
      return;
    }

    if (current === 'central' && this.central().length) {
      return;
    }

    if (current === 'right' && this.right().length) {
      return;
    }

    if (this.central().length) {
      this._openSection.set('central');
      return;
    }

    if (this.left().length) {
      this._openSection.set('left');
      return;
    }

    if (this.right().length) {
      this._openSection.set('right');
      return;
    }

    this._openSection.set(null);
  }

  private getTranslation(key: string): string {
    return this._translate.instant(key);
  }
}
